import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { PageHero } from "../components/PageHero";
import { Reveal } from "../components/Reveal";
import { PHOTOS } from "../lib/config";

const CHAPTERS = [
  {
    num: "01",
    title: "A foundation in public service",
    body: [
      "Jeff Trickler is seeking reelection as Ontario County Treasurer. His experience as County Treasurer, combined with prior public service as Chief of Police for the City of Geneva, has given him the leadership, financial oversight, and administrative expertise to succeed in the role.",
    ],
  },
  {
    num: "02",
    title: "Leadership tested at scale",
    body: [
      "As Chief of Police, he oversaw a $4 million annual budget and held complete organizational leadership of a 50-person department. He managed all human resource functions within the department and advised the Mayor, City Manager, and Human Resource Director on citywide policies and procedures.",
      "As the department's Public Information Officer, he was responsible for communicating with residents and other government entities on departmental matters.",
    ],
  },
  {
    num: "03",
    title: "The work of the Treasurer's Office",
    body: [
      "That range of experience has proven invaluable as County Treasurer. The Treasurer's Office handles many critical functions of county government, including coordinating tax collection with all towns, villages, and cities; managing property foreclosures; providing court testimony; conducting tax searches; issuing certificates of residency; and processing PILOT and corporation payments, among other responsibilities.",
      "His four-plus years in the office have set the standard for how the department fulfills its obligations to taxpayers.",
    ],
  },
  {
    num: "04",
    title: "Why he's running",
    body: [
      "He is running for reelection because public service has always been his commitment. He leads with fairness, consistency, and accountability, is an effective communicator, and holds the confidence of the other county departments that work closely with the Treasurer's Office.",
    ],
  },
];

export default function About() {
  return (
    <div data-testid="about-page">
      <PageHero
        eyebrow="About the Candidate"
        title="Meet Jeff Trickler"
        intro="Four-plus years of proven leadership in the Ontario County Treasurer's Office — built on a career of dedicated public service."
        testid="about-hero"
      />

      {/* Portrait + lede */}
      <section className="py-20 md:py-28 bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-5 md:px-8 grid md:grid-cols-12 gap-12 items-start">
          <Reveal className="md:col-span-5">
            <div className="overflow-hidden clip-frame border border-slate-200 sticky top-28">
              <img
                src={PHOTOS.headshot}
                alt="Jeffrey Trickler, Ontario County Treasurer"
                className="w-full object-cover"
              />
            </div>
            <p className="mt-3 text-xs text-slate-400 italic">
              Slot reserved for Jeff's official campaign headshot &amp; photos.
            </p>
          </Reveal>

          <div className="md:col-span-7">
            {CHAPTERS.map((ch, i) => (
              <Reveal key={ch.num} delay={i * 0.05} className="mb-14 last:mb-0">
                <div className="flex items-baseline gap-5">
                  <span className="font-mono-num text-2xl font-bold text-red-700">{ch.num}</span>
                  <div className="h-px flex-1 bg-slate-200 translate-y-[-4px]" />
                </div>
                <h2 className="mt-4 font-display text-3xl md:text-4xl font-bold tracking-tight text-[hsl(var(--navy))]">
                  {ch.title}
                </h2>
                {ch.body.map((p, j) => (
                  <p key={j} className="mt-4 text-lg text-slate-600 leading-relaxed">
                    {p}
                  </p>
                ))}
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 md:py-28 bg-background">
        <div className="max-w-5xl mx-auto px-5 md:px-8 text-center">
          <Reveal>
            <h2 className="font-display text-3xl md:text-5xl font-bold tracking-tight text-[hsl(var(--navy))] text-balance">
              See what that experience has delivered.
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/accomplishments"
                data-testid="about-accomplishments-btn"
                className="focus-ring group inline-flex items-center justify-center gap-2 bg-[hsl(var(--navy))] text-white font-semibold px-8 py-4 rounded-sm hover:bg-slate-800 transition-colors"
              >
                View Accomplishments
                <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link
                to="/donate"
                data-testid="about-donate-btn"
                className="focus-ring inline-flex items-center justify-center gap-2 border border-slate-300 text-[hsl(var(--navy))] font-semibold px-8 py-4 rounded-sm hover:bg-white transition-colors"
              >
                Support the Campaign
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
